/**
 * ─── Laravel Backend API Client ──────────────────────────────────────
 *
 * Sole API client. All data served from local SQLite via Laravel.
 * No more direct Onyx calls or IndexedDB caching.
 */
import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  timeout: 30_000,
})

// ── Auth interceptor: attach Bearer token to all requests ────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ── 401 interceptor: redirect to login on auth failure ───────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      // Only redirect if not already on login page
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  },
)

// ── Response Meta (added by ApiResponseWrapper middleware) ────────────

export interface ResponseMeta {
  last_synced_at: string | null
  data_source: string
  response_time_ms: number
}

// ── Paginated response shape from Laravel ────────────────────────────

export interface PaginatedResponse<T> {
  data: T[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number | null
  to: number | null
  meta?: ResponseMeta
}

// ── Dashboard ────────────────────────────────────────────────────────

export interface DashboardKpis {
  total_sales: number
  total_sales_change: number
  total_orders: number
  total_orders_change: number
  active_customers: number
  active_customers_change: number
  avg_order_value: number
  avg_order_value_change: number
}

export interface DashboardTopRep {
  code: string
  name: string
  total_sales: number
  total_orders: number
  customer_count: number
}

export interface DashboardTopCustomer {
  code: string
  name: string
  balance: number
  total_purchases: number
  order_count: number
}

export interface DashboardTrendItem {
  month: string
  sales: number
  orders: number
  customers: number
}

export interface DashboardWarehouse {
  code: string
  name: string
  total: number
  order_count: number
}

export interface DashboardRecentOrder {
  order_ser: string
  date: string
  customer_code: string
  customer_name: string
  total: number
  status: string
  item_count: number
}

export interface DashboardResponse {
  kpis: DashboardKpis
  top_reps: DashboardTopRep[]
  top_customers: DashboardTopCustomer[]
  sales_trend: DashboardTrendItem[]
  sales_by_warehouse: DashboardWarehouse[]
  recent_orders: DashboardRecentOrder[]
  meta?: ResponseMeta
}

// ── Orders ───────────────────────────────────────────────────────────

export interface OrderListItem {
  order_ser: string
  order_no: string
  order_date: string
  customer_code: string
  customer_name: string
  rep_name: string
  rep_code: string
  warehouse_name: string
  warehouse_code: string
  total: number
  total_qty: number
  item_count: number
  status: string
  year: number
  currency: string
}

export interface OrderDetailItem {
  id: number
  order_ser: string
  item_code: string
  item_name: string
  qty: number
  unit_price: number
  total_amt: number
  unit_name: string
}

export interface OrderDetailResponse {
  order: {
    order_ser: string
    order_no: string
    order_date: string
    customer_code: string
    customer_name: string
    warehouse_code: string
    total: number
    total_qty: number
    item_count: number
    status: string
    year: number
    rep_code: string
    notes: string
    details: OrderDetailItem[]
    customer?: {
      code: string
      name_ar: string
      rep_code: string
      rep?: { code: string; name_ar: string }
      city?: { city_no: number; name_ar: string }
    }
  }
  customer_name: string
  rep_name: string
  warehouse_name: string
  meta?: ResponseMeta
}

// ── Customers ────────────────────────────────────────────────────────

export interface CustomerListItem {
  code: string
  name_ar: string
  name_en: string
  balance: number
  credit_limit: number
  rep_code: string
  city_no: number
  phone: string
  is_active: boolean
  rep_name: string
  city: string
  total_purchases: number
  total_orders: number
  avg_order_value: number
  last_order_date: string | null
}

export interface CustomerProfileOrder {
  order_ser: string
  order_date: string
  customer_code: string
  total: number
  item_count: number
  status: string
}

export interface CustomerMonthly {
  month: string
  sales: number
  orders: number
}

export interface CustomerProfileResponse {
  customer: {
    code: string
    name_ar: string
    name_en: string
    balance: number
    credit_limit: number
    rep_code: string
    city_no: number
    phone: string
    address: string
  }
  rep_name: string
  city_name: string
  metrics: {
    total_purchases: number
    total_orders: number
    avg_order_value: number
    last_order_date: string | null
  }
  orders: PaginatedResponse<CustomerProfileOrder>
  monthly: CustomerMonthly[]
  meta?: ResponseMeta
}

// ── Reps ─────────────────────────────────────────────────────────────

export interface RepListItem {
  code: string
  name_ar: string
  name_en: string
  is_active: boolean
  city_no: number
  warehouse_code: string
  phone: string
  city: string
  warehouse: string
  total_sales: number
  total_orders: number
  customer_count: number
  avg_order_value: number
  last_order_date: string | null
}

export interface RepTopCustomer {
  code: string
  name_ar: string
  total_purchases: number
  order_count: number
  balance: number
}

export interface RepMonthly {
  month: string
  sales: number
  orders: number
  customers: number
}

export interface RepProfileResponse {
  rep: {
    code: string
    name_ar: string
    name_en: string
    is_active: boolean
    city_no: number
    warehouse_code: string
    phone: string
  }
  city_name: string
  metrics: {
    total_sales: number
    total_orders: number
    avg_order_value: number
    customer_count: number
  }
  monthly: RepMonthly[]
  top_customers: RepTopCustomer[]
  orders: PaginatedResponse<{
    order_ser: string
    order_date: string
    customer_code: string
    customer_name: string
    total: number
    status: string
  }>
  meta?: ResponseMeta
}

// ── Analytics ────────────────────────────────────────────────────────

export interface AnalyticsPeriod {
  sales: number
  orders: number
  customers: number
}

export interface AnalyticsWarehouse {
  code: string
  name: string
  total: number
  order_count: number
  pct: number
}

export interface AnalyticsCity {
  city_no: number
  name: string
  total: number
  order_count: number
  pct: number
}

export interface AnalyticsTierInfo {
  count: number
  total_sales: number
}

export interface AnalyticsTopItem {
  item_code: string
  item_name: string
  total_qty: number
  total_revenue: number
  order_count: number
}

export interface AnalyticsMonthly {
  month: string
  sales: number
  orders: number
  customers: number
}

export interface AnalyticsSummaryResponse {
  period_comparison: {
    current_period: AnalyticsPeriod
    previous_period: AnalyticsPeriod
    changes: {
      sales_pct: number
      orders_pct: number
      customers_pct: number
    }
  }
  sales_by_warehouse: AnalyticsWarehouse[]
  sales_by_city: AnalyticsCity[]
  customer_tiers: Record<'platinum' | 'gold' | 'silver' | 'bronze', AnalyticsTierInfo>
  top_items: AnalyticsTopItem[]
  monthly_trend: AnalyticsMonthly[]
  meta?: ResponseMeta
}

// ── Sync ─────────────────────────────────────────────────────────────

export interface SyncLog {
  id: number
  type: string
  status: string
  records_synced: number
  orders_synced: number
  details_synced: number
  balances_synced: number
  duration_seconds: number | null
  last_order_date: string | null
  started_at: string
  completed_at: string | null
}

export interface SyncStatusResponse {
  last_sync: SyncLog | null
  database_stats: {
    total_orders: number
    total_customers: number
    total_reps: number
    total_warehouses: number
    total_cities: number
    total_order_details: number
    orders_with_details: number
    oldest_order: string | null
    newest_order: string | null
  }
  next_sync_at: string | null
  recent_logs: SyncLog[]
  cooldown_remaining: number
  meta?: ResponseMeta
}

// ── API Functions ────────────────────────────────────────────────────

export interface FilterParams {
  year?: string
  month?: string
}

export interface PaginationParams extends FilterParams {
  page?: number
  per_page?: number
  sort?: string
  dir?: 'asc' | 'desc'
  search?: string
}

/** Extract meta from any response */
function extractMeta(data: any): ResponseMeta | undefined {
  return data?.meta
}

// Dashboard
export async function fetchDashboard(params: FilterParams = {}): Promise<DashboardResponse> {
  const { data } = await api.get<DashboardResponse>('/dashboard', { params })
  return data
}

// Orders
export async function fetchOrders(params: PaginationParams & {
  customer_code?: string
  rep_code?: string
  warehouse_code?: string
  status?: string
} = {}): Promise<PaginatedResponse<OrderListItem>> {
  const { data } = await api.get<PaginatedResponse<OrderListItem>>('/orders', { params })
  return data
}

export async function fetchOrder(orderSer: string): Promise<OrderDetailResponse> {
  const { data } = await api.get<OrderDetailResponse>(`/orders/${orderSer}`)
  return data
}

// Customers
export async function fetchCustomers(params: PaginationParams & {
  rep_code?: string
  city_no?: number
} = {}): Promise<PaginatedResponse<CustomerListItem>> {
  const { data } = await api.get<PaginatedResponse<CustomerListItem>>('/customers', { params })
  return data
}

export async function fetchCustomer(code: string, params: FilterParams & {
  per_page?: number
  page?: number
} = {}): Promise<CustomerProfileResponse> {
  const { data } = await api.get<CustomerProfileResponse>(`/customers/${code}`, { params })
  return data
}

// ── Customer classification reports (العملاء) ─────────────────────────

export interface CustomerReportPeriod {
  year: number
  month: number
  batch_id: number | null
}

export interface ReportRep {
  owner_id: string
  /** Full employee name (headline). */
  owner_name?: string
  /** Rep short-name shown beneath, when distinct from owner_name. */
  owner_rep?: string | null
  sales_rep_name: string | null
  employee_name: string | null
  total_due: number
  /** Share of the period-wide total customer due (%). */
  due_pct?: number
  /** Sum of the owner's customers' متوسط السداد الربعي (for sorting). */
  quarterly_payment_average?: number
  total_sales: number
  customer_count: number
  /** Project debt percentages: project name -> percentage of this owner's debt. */
  project_debt_pcts?: Record<string, number>
}

export interface ReportRepsResponse {
  reps: ReportRep[]
  /** Period-wide total customer due — the denominator behind each rep's due_pct. */
  grand_total_due?: number
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

export interface ReportBucket {
  key: string
  label: string
  customer_count: number
  total_due: number
  total_sales: number
  debt_pct: number
}

export interface ReportBucketsResponse {
  buckets: ReportBucket[]
  total_due: number
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

export interface ReportSize {
  level: number
  label: string
  customer_count: number
  total_due: number
  total_sales: number
}

export interface ReportSizesResponse {
  sizes: ReportSize[]
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

export interface ReportCity {
  city_id: string
  city: string
  total_due: number
  total_sales: number
  customer_count: number
}

export interface ReportCitiesResponse {
  cities: ReportCity[]
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

export interface ReportCustomerListItem {
  customer_id: string
  customer_name: string
  customer_number?: string | null
  /** تاريخ التسجيل (Y-m-d) from the ONYX import. */
  registration_date?: string | null
  /** رقم الجوال (falls back to الهاتف server-side). */
  phone?: string | null
  /** Registered within the configurable جديد window (قواعد التقييم). */
  is_new?: boolean
  sales_rep_name: string | null
  employee_name: string | null
  city: string | null
  debt_amount: number
  quarterly_payment_average: number
  total_sales: number
  total_score: number
  purchase_points: number
  bucket_key: string
  bucket_label: string
}

/** Filters shared by every drill endpoint. */
export interface ReportFilters {
  owner?: string
  bucket?: string
  size?: number
  city?: string
  /**
   * مركز التكلفة — membership filter applied across all العملاء screens.
   * Multi-select: an array is sent as `cost_center[]=…` and OR-ed server-side.
   */
  cost_center?: string | string[]
  /** اسم المشروع — membership filter applied across all العملاء screens. */
  project?: string | string[]
  /** اسم المجموعة — membership filter applied across all العملاء screens. */
  group?: string | string[]
  /** Sort field + direction (المديونية / متوسط السداد الربعي …). */
  sort?: string
  dir?: 'asc' | 'desc'
}

/** One selectable cost center (مركز التكلفة) for the shared filter. */
export interface ReportCostCenter {
  id: string
  name: string
}

export interface ReportCostCentersResponse {
  cost_centers: ReportCostCenter[]
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

/** One selectable project (اسم المشروع) for the shared filter. */
export interface ReportProject {
  id: string
  name: string
}

export interface ReportProjectsResponse {
  projects: ReportProject[]
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

/** One selectable customer group (اسم المجموعة) for the shared filter. */
export interface ReportGroup {
  id: string
  name: string
}

export interface ReportGroupsResponse {
  groups: ReportGroup[]
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

// ── Debts / aging (الديون) ────────────────────────────────────────────

/** Aging buckets + share, shared by the totals, owner and customer rows. */
export interface DebtAgingFigures {
  total_due: number
  debt_0_30: number
  debt_31_60: number
  debt_61_90: number
  debt_91_120: number
  debt_over_120: number
  debt_over_90: number
  due_pct: number
  over_90_rate: number
  over_120_rate: number
}

export interface DebtAgingOwner extends DebtAgingFigures {
  owner_id: string
  /** Full employee name (headline). */
  owner_name: string
  /** Rep short-name shown beneath, when distinct from owner_name. */
  owner_rep?: string | null
  customer_count: number
}

export interface DebtAgingCustomer extends DebtAgingFigures {
  customer_id: string
  customer_name: string
}

export interface DebtAgingResponse {
  totals: DebtAgingFigures
  grand_total_due: number
  /** Present on the top-level (no owner) response. */
  owners?: DebtAgingOwner[]
  /** Present when drilling into a single owner. */
  owner?: string
  customers?: DebtAgingCustomer[]
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

/** One customer behind an aging card (the modal). */
export interface DebtAgingBucketCustomer {
  customer_id: string
  customer_name: string
  owner_name: string
  owner_rep?: string | null
  /** The card's own amount for this customer. */
  amount: number
  /** The customer's full المديونية. */
  total_due: number
}

export interface DebtAgingBucketResponse {
  totals: DebtAgingFigures
  grand_total_due: number
  bucket: string
  bucket_total: number
  customer_count: number
  customers: DebtAgingBucketCustomer[]
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

export interface CustomerScorecard {
  customer_id: string
  customer_name: string
  customer_number?: string | null
  /** تاريخ التسجيل (Y-m-d). */
  registration_date?: string | null
  /** Whole days since registration (null when unknown / bad data). */
  days_since_registration?: number | null
  /** جديد — registered within the configurable window. */
  is_new?: boolean
  /** رقم الجوال (server falls back to الهاتف). */
  phone?: string | null
  /** آخر حركة — display only. */
  last_invoice_amount?: number | null
  last_sale_date?: string | null
  last_payment_amount?: number | null
  last_payment_date?: string | null
  sales_rep_id: string | null
  sales_rep_name: string | null
  employee_id: string | null
  employee_name: string | null
  total_sales: number
  net_sales_2023: number
  net_sales_2024: number
  net_sales_2025: number
  best_quarter_sales: number
  avg_sales_90d: number
  debt_amount: number
  debt_age_days: number
  quarterly_payment_average: number
  credit_limit: number
  /** Purchasing-strength baseline: leader avg_sales_90d, or the cost-center goal amount when pct_basis = 'goal'. */
  leader_payment: number
  purchase_pct: number
  /** Which baseline produced purchase_pct: the global leader or the primary cost center's goal. */
  pct_basis?: 'leader' | 'goal'
  goal_period_label?: string | null
  primary_cost_center?: string | null
  customer_size: number
  best_level_date: string | null
  monthly_sales: number
  monthly_payment: number
  /** مبلغ التحصيل (إجمالي مبلغ التحصيل). */
  collection_amount: number
  monthly_sales_target: number
  monthly_payment_target: number
  returns_total: number
  returns_new: number
  returns_rate_total: number | null
  returns_rate_new: number | null
  purchase_points: number
  commitment_points: number
  risk_points: number
  risk_ratio: number | null
  total_score: number
  bucket_key: string
  bucket_label: string
  period: string
}

export interface CustomerScorecardResponse {
  customer: CustomerScorecard
  period: CustomerReportPeriod | null
  meta?: ResponseMeta
}

export async function fetchReportReps(
  params: ReportFilters = {},
): Promise<ReportRepsResponse> {
  const { data } = await api.get<ReportRepsResponse>('/customer-reports/reps', { params })
  return data
}

export async function fetchReportBuckets(
  params: ReportFilters = {},
): Promise<ReportBucketsResponse> {
  const { data } = await api.get<ReportBucketsResponse>('/customer-reports/buckets', { params })
  return data
}

export async function fetchReportSizes(
  params: ReportFilters = {},
): Promise<ReportSizesResponse> {
  const { data } = await api.get<ReportSizesResponse>('/customer-reports/sizes', { params })
  return data
}

export async function fetchReportCities(
  params: ReportFilters = {},
): Promise<ReportCitiesResponse> {
  const { data } = await api.get<ReportCitiesResponse>('/customer-reports/cities', { params })
  return data
}

export async function fetchReportCostCenters(
  params: ReportFilters = {},
): Promise<ReportCostCentersResponse> {
  const { data } = await api.get<ReportCostCentersResponse>('/customer-reports/cost-centers', { params })
  return data
}

export async function fetchReportProjects(
  params: ReportFilters = {},
): Promise<ReportProjectsResponse> {
  const { data } = await api.get<ReportProjectsResponse>('/customer-reports/projects', { params })
  return data
}

export async function fetchReportGroups(
  params: ReportFilters = {},
): Promise<ReportGroupsResponse> {
  const { data } = await api.get<ReportGroupsResponse>('/customer-reports/groups', { params })
  return data
}

export async function fetchReportDebtAging(
  params: ReportFilters & { owner?: string } = {},
): Promise<DebtAgingResponse> {
  const { data } = await api.get<DebtAgingResponse>('/customer-reports/debt-aging', { params })
  return data
}

/** Customers behind one aging card (the debts-screen modal). */
export async function fetchReportDebtAgingBucket(
  bucket: string,
  params: ReportFilters = {},
): Promise<DebtAgingBucketResponse> {
  const { data } = await api.get<DebtAgingBucketResponse>('/customer-reports/debt-aging', {
    params: { ...params, bucket },
  })
  return data
}

export async function fetchReportCustomers(
  params: PaginationParams & ReportFilters = {},
): Promise<PaginatedResponse<ReportCustomerListItem>> {
  const { data } = await api.get<PaginatedResponse<ReportCustomerListItem>>('/customer-reports/customers', { params })
  return data
}

export async function fetchReportCustomer(
  customerId: string,
  params: FilterParams = {},
): Promise<CustomerScorecardResponse> {
  const { data } = await api.get<CustomerScorecardResponse>(`/customer-reports/customers/${encodeURIComponent(customerId)}`, { params })
  return data
}

// ── Rating rules "smart helper" + save ────────────────────────────────

export interface RatingRuleBand {
  threshold: number
  points?: number
  bucket_key?: string
  bucket_label?: string
}

export interface RatingRuleSet {
  purchase: RatingRuleBand[]
  commitment: RatingRuleBand[]
  risk: RatingRuleBand[]
  bucket: RatingRuleBand[]
  /** مفقود payment-health override. */
  payment_health: RatingRuleBand[]
  /** جديد — new-customer window in days (single band). */
  new_customer: RatingRuleBand[]
}

export interface RatingCategoryMeta {
  key: 'purchase' | 'commitment' | 'risk' | 'bucket' | 'payment_health' | 'new_customer'
  label: string
  operator: 'gte' | 'lte'
  op_symbol: string
  value_label: string
  unit: string
  kind: 'points' | 'bucket' | 'payment_health' | 'new_customer'
  max?: number
  hint?: string
}

export interface RatingRulesResponse {
  rules: RatingRuleSet
  defaults: RatingRuleSet
  categories: RatingCategoryMeta[]
  has_saved: boolean
}

export interface RatingPreviewCustomer {
  customer_id: string
  customer_name: string
  sales_rep_name: string | null
  employee_name: string | null
  city: string | null
  total_sales: number
  debt_amount: number
  quarterly_payment_average: number
  customer_size: number
  total_score: number
  bucket_key: string
  bucket_label: string
}

export interface RatingPreviewSummary {
  total: number
  period: string | null
  buckets: { key: string; label: string; count: number; total_due: number }[]
  sizes: { level: number; count: number }[]
  customers: RatingPreviewCustomer[]
  customers_total: number
}

export interface RatingSaveSummary {
  saved: number
  period: string | null
  buckets: { key: string; label: string; count: number }[]
}

export interface RatingRulesPayload {
  rules?: Partial<RatingRuleSet>
}

export async function fetchRatingRules(): Promise<RatingRulesResponse> {
  const { data } = await api.get<{ data: RatingRulesResponse }>('/customer-reports/rules')
  return data.data
}

export async function previewRatings(payload: RatingRulesPayload): Promise<RatingPreviewSummary> {
  const { data } = await api.post<{ data: RatingPreviewSummary }>('/customer-reports/ratings/preview', payload)
  return data.data
}

export async function saveRatings(payload: RatingRulesPayload): Promise<RatingSaveSummary> {
  const { data } = await api.post<{ data: RatingSaveSummary }>('/customer-reports/ratings/save', payload)
  return data.data
}

export async function exportRatings(payload: RatingRulesPayload): Promise<{ blob: Blob; filename: string }> {
  const res = await api.post('/customer-reports/ratings/export', payload, { responseType: 'blob' })
  const disp = (res.headers['content-disposition'] as string | undefined) ?? ''
  const match = disp.match(/filename="?([^";]+)"?/)
  return { blob: res.data as Blob, filename: match ? match[1] : 'customer-ratings.xlsx' }
}

// أهداف مراكز التكلفة — per-cost-center goal replacing the leader baseline (admin only).
export interface CostCenterGoal {
  id: number
  cost_center_name: string
  period_type: 'year' | 'quarter'
  period_value: number
  goal_amount: number
  is_active: boolean
}

export type CostCenterGoalPayload = Omit<CostCenterGoal, 'id'>

export async function fetchCostCenterGoals(): Promise<CostCenterGoal[]> {
  const { data } = await api.get<{ data: CostCenterGoal[] }>('/customer-reports/goals')
  return data.data
}

export async function createCostCenterGoal(payload: CostCenterGoalPayload): Promise<CostCenterGoal> {
  const { data } = await api.post<{ data: CostCenterGoal }>('/customer-reports/goals', payload)
  return data.data
}

export async function updateCostCenterGoal(id: number, payload: CostCenterGoalPayload): Promise<CostCenterGoal> {
  const { data } = await api.put<{ data: CostCenterGoal }>(`/customer-reports/goals/${id}`, payload)
  return data.data
}

export async function deleteCostCenterGoal(id: number): Promise<void> {
  await api.delete(`/customer-reports/goals/${id}`)
}

export async function toggleCostCenterGoal(id: number): Promise<CostCenterGoal> {
  const { data } = await api.post<{ data: CostCenterGoal }>(`/customer-reports/goals/${id}/toggle`)
  return data.data
}

// Reps
export async function fetchReps(params: PaginationParams & {
  city_no?: number
  warehouse_code?: string
} = {}): Promise<PaginatedResponse<RepListItem>> {
  const { data } = await api.get<PaginatedResponse<RepListItem>>('/reps', { params })
  return data
}

export async function fetchRep(code: string, params: FilterParams & {
  per_page?: number
  page?: number
} = {}): Promise<RepProfileResponse> {
  const { data } = await api.get<RepProfileResponse>(`/reps/${code}`, { params })
  return data
}

// Analytics
export async function fetchAnalyticsSummary(params: FilterParams = {}): Promise<AnalyticsSummaryResponse> {
  const { data } = await api.get<AnalyticsSummaryResponse>('/analytics/summary', { params })
  return data
}

export async function fetchAnalyticsItems(params: PaginationParams = {}): Promise<PaginatedResponse<AnalyticsTopItem>> {
  const { data } = await api.get<PaginatedResponse<AnalyticsTopItem>>('/analytics/items', { params })
  return data
}

// Sync
export async function fetchSyncStatus(): Promise<SyncStatusResponse> {
  const { data } = await api.get<SyncStatusResponse>('/sync/status')
  return data
}

export async function triggerSync(type: 'incremental' | 'full' | 'balances' | 'fix-orders' = 'incremental'): Promise<{ message: string; log?: SyncLog }> {
  const { data } = await api.post('/sync', { type })
  return data
}

export { extractMeta }

// ── Admin: User Management ───────────────────────────────────────────

export interface AdminUser {
  id: number
  name: string
  display_name: string | null
  email: string | null
  role: string
  created_at: string
}

export interface CreateUserPayload {
  name: string
  display_name?: string
  email?: string
  password: string
  role: 'admin' | 'manager' | 'viewer'
}

export interface UpdateUserPayload {
  display_name?: string
  email?: string
  password?: string
  role?: 'admin' | 'manager' | 'viewer'
}

export async function fetchUsers(): Promise<AdminUser[]> {
  const { data } = await api.get<AdminUser[]>('/admin/users')
  return data
}

export async function createUser(payload: CreateUserPayload): Promise<{ message: string; user: AdminUser }> {
  const { data } = await api.post('/admin/users', payload)
  return data
}

export async function updateUser(id: number, payload: UpdateUserPayload): Promise<{ message: string; user: AdminUser }> {
  const { data } = await api.put(`/admin/users/${id}`, payload)
  return data
}

export async function deleteUser(id: number): Promise<{ message: string }> {
  const { data } = await api.delete(`/admin/users/${id}`)
  return data
}

// ── Auth ─────────────────────────────────────────────────────────────

export type LoginType = 'admin' | 'sales_rep' | 'employee'

export interface AuthUserPayload {
  id: string
  username: string
  displayName: string
  email: string
  role: string
  permissions?: string[]
  sales_rep_code?: string | null
  employee_id?: string | null
  must_change_password?: boolean
  is_active?: boolean
}

export interface LoginResponse {
  user: AuthUserPayload
  token: string
}

export interface MeResponse {
  user: AuthUserPayload
}

export async function apiLogin(
  username: string,
  password: string,
  loginType: LoginType = 'admin',
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/auth/login', {
    username,
    password,
    login_type: loginType,
  })
  return data
}

export async function apiLogout(): Promise<void> {
  await api.post('/auth/logout')
}

export async function apiMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>('/auth/me')
  return data
}

export async function apiChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string; user: AuthUserPayload }> {
  const { data } = await api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
    new_password_confirmation: newPassword,
  })
  return data
}
